import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { MoreVertical, Pencil, Trash2, Share2, Plus, User, MoreHorizontal, Eye } from 'lucide-react';
import { fetchUmpires, deleteUmpire } from '@/services/supabaseService';
import { Umpire } from '@/integrations/supabase/types';
import { toast } from '@/components/ui/use-toast';
import ViewToggle from '@/components/common/ViewToggle';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import UniqueIdDisplay from '@/components/common/UniqueIdDisplay';
import { Checkbox } from '@/components/ui/checkbox';
import SortableTableHeader from '@/components/common/SortableTableHeader';
import EditUmpireModal from './EditUmpirePage';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

const UmpiresPage = () => {
  const [umpires, setUmpires] = useState<Umpire[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPanel, setFilterPanel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'card' | 'list'>('card');
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedUmpires, setSelectedUmpires] = useState<string[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUmpireId, setSelectedUmpireId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    const loadUmpires = async () => {
      setLoading(true);
      try {
        const data = await fetchUmpires();
        setUmpires(data);
      } catch (error: any) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
      }
      setLoading(false);
    };
    loadUmpires();
  }, []);

  const uniquePanels = Array.from(new Set(umpires.map(u => u.panelType).filter(Boolean)));
  const uniqueStatuses = Array.from(new Set(umpires.map(u => u.status).filter(Boolean)));

  const filteredUmpires = umpires.filter(u => {
    const matchesSearch =
      (u.firstName + ' ' + u.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.nationality || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPanel = filterPanel === 'all' || u.panelType === filterPanel;
    const matchesStatus = filterStatus === 'all' || u.status === filterStatus;
    return matchesSearch && matchesPanel && matchesStatus;
  });

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedUmpires = React.useMemo(() => {
    const arr = [...filteredUmpires];
    arr.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      if (sortKey === 'name') {
        aValue = (a.firstName + ' ' + a.lastName).toLowerCase();
        bValue = (b.firstName + ' ' + b.lastName).toLowerCase();
      } else if (sortKey === 'createdAt' || sortKey === 'updatedAt') {
        aValue = a[sortKey] ? new Date(a[sortKey] as string).getTime() : 0;
        bValue = b[sortKey] ? new Date(b[sortKey] as string).getTime() : 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        aValue = a[sortKey];
        bValue = b[sortKey];
      }
      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue, 'en-US', { sensitivity: 'base' });
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue), 'en-US', { sensitivity: 'base' });
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return arr;
  }, [filteredUmpires, sortKey, sortDirection]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUmpires(sortedUmpires.map(u => u.umpireId));
    } else {
      setSelectedUmpires([]);
    }
  };

  const handleSelectUmpire = (umpireId: string, checked: boolean) => {
    if (checked) {
      setSelectedUmpires([...selectedUmpires, umpireId]);
    } else {
      setSelectedUmpires(selectedUmpires.filter(id => id !== umpireId));
    }
  };

  const handleDeleteUmpire = async (umpireId: string) => {
    const id = String(umpireId).trim();
    console.log('Attempting to delete umpire with id:', id);
    const umpire = umpires.find(u => u.umpireId === umpireId);
    try {
      await deleteUmpire(id);
      setUmpires(umpires.filter(u => u.umpireId !== umpireId));
      toast({ title: 'Umpire deleted', description: `${umpire ? umpire.firstName + ' ' + umpire.lastName : 'The umpire'} has been removed.` });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Umpires</h1>
          <p className="text-gray-500">Manage cricket umpires</p>
        </div>
        <Link to="/umpires/new">
          <Button className="bg-cricket-green">
            <Plus size={16} className="mr-2" /> Add Umpire
          </Button>
        </Link>
      </div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative flex-[2] w-full">
          <User className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search umpires..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-8 w-full"
          />
        </div>
        <Select onValueChange={setFilterPanel} value={filterPanel}>
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="All Panels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Panels</SelectItem>
            {uniquePanels.map(panel => (
              <SelectItem key={panel} value={panel}>{panel}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select onValueChange={setFilterStatus} value={filterStatus}>
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {uniqueStatuses.map(status => (
              <SelectItem key={status} value={status}>{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="shrink-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleSort('name')} className="flex items-center justify-between">
              <span>Name</span>
              {sortKey === 'name' && (
                <span className="ml-2 text-muted-foreground">{sortDirection === 'asc' ? '↑' : '↓'}</span>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSort('createdAt')} className="flex items-center justify-between">
              <span>Created Date</span>
              {sortKey === 'createdAt' && (
                <span className="ml-2 text-muted-foreground">{sortDirection === 'asc' ? '↑' : '↓'}</span>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSort('updatedAt')} className="flex items-center justify-between">
              <span>Modified Date</span>
              {sortKey === 'updatedAt' && (
                <span className="ml-2 text-muted-foreground">{sortDirection === 'asc' ? '↑' : '↓'}</span>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}>
              Toggle Sort Direction
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ViewToggle view={view} onViewChange={setView} />
      </div>
      {loading ? (
        <div className="text-center text-gray-500 py-12">Loading...</div>
      ) : sortedUmpires.length > 0 ? (
        view === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedUmpires.map(umpire => (
              <Card key={umpire.umpireId} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start flex-wrap">
                    <div className="flex-1 flex items-center gap-4 min-w-0">
                      {umpire.profilePhoto ? (
                        <img
                          src={umpire.profilePhoto}
                          alt={umpire.firstName + ' ' + umpire.lastName}
                          className="w-12 h-12 object-cover rounded-full border-2 border-gray-200 bg-white shadow-sm"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-12 h-12 rounded-full text-white font-bold text-xl uppercase select-none bg-gray-400">
                          {umpire.firstName?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <CardTitle className="flex items-center gap-2">
                          <span className="block truncate max-w-[180px] cursor-pointer">{umpire.firstName} {umpire.lastName}</span>
                        </CardTitle>
                        <CardDescription>{umpire.primaryRole || umpire.panelType}</CardDescription>
                      </div>
                    </div>
                    <div className="flex flex-row items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded hover:bg-muted transition-colors" title="More actions">
                            <MoreVertical size={20} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              const url = `${window.location.origin}/umpires/${umpire.umpireId}`;
                              navigator.clipboard.writeText(url);
                              toast({ title: 'Profile link copied!', description: 'Umpire profile URL copied to clipboard.' });
                            }}
                          >
                            <Share2 size={16} className="mr-2" /> Share
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUmpireId(umpire.umpireId);
                              setEditModalOpen(true);
                            }}
                          >
                            <Pencil size={16} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600" onClick={() => setConfirmDeleteId(umpire.umpireId)}>
                            <Trash2 size={16} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Nationality: {umpire.nationality || '—'}</p>
                </CardHeader>
                <CardContent>
                  {/* Add more umpire details here if needed */}
                </CardContent>
                <div className="px-6 pb-4">
                  <Link to={`/umpires/${umpire.umpireId}`}>
                    <Button variant="outline" className="w-full">View Details</Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px] text-center">
                    <Checkbox
                      checked={selectedUmpires.length === sortedUmpires.length && sortedUmpires.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <SortableTableHeader
                    label="Umpire Name"
                    sortKey="name"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[150px]"
                  />
                  <SortableTableHeader
                    label="Panel Type"
                    sortKey="panelType"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[120px]"
                  />
                  <SortableTableHeader
                    label="Status"
                    sortKey="status"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[120px]"
                  />
                  <SortableTableHeader
                    label="Nationality"
                    sortKey="nationality"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[120px]"
                  />
                  <SortableTableHeader
                    label="Unique ID"
                    sortKey="umpireId"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[150px]"
                  />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUmpires.map(umpire => {
                  const isSelected = selectedUmpires.includes(umpire.umpireId);
                  return (
                    <TableRow key={umpire.umpireId}>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => handleSelectUmpire(umpire.umpireId, checked as boolean)}
                        />
                      </TableCell>
                      <TableCell className="font-medium flex items-center gap-2">
                        {umpire.profilePhoto ? (
                          <img
                            src={umpire.profilePhoto}
                            alt={umpire.firstName + ' ' + umpire.lastName}
                            className="w-8 h-8 object-cover rounded-full border border-gray-200 bg-white"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-white font-bold text-sm uppercase select-none bg-gray-400">
                            {umpire.firstName?.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span>{umpire.firstName} {umpire.lastName}</span>
                      </TableCell>
                      <TableCell>{umpire.panelType || '—'}</TableCell>
                      <TableCell>{umpire.status || '—'}</TableCell>
                      <TableCell>{umpire.nationality || '—'}</TableCell>
                      <TableCell><UniqueIdDisplay uniqueId={umpire.umpireId} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-row items-center gap-2 justify-end">
                          <Link to={`/umpires/${umpire.umpireId}`} className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium transition-colors">
                            <Eye size={16} className="mr-1" /> View
                          </Link>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 rounded hover:bg-muted transition-colors" title="More actions">
                                <MoreVertical size={20} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  const url = `${window.location.origin}/umpires/${umpire.umpireId}`;
                                  navigator.clipboard.writeText(url);
                                  toast({ title: 'Profile link copied!', description: 'Umpire profile URL copied to clipboard.' });
                                }}
                              >
                                <Share2 size={16} className="mr-2" /> Share
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUmpireId(umpire.umpireId);
                                  setEditModalOpen(true);
                                }}
                              >
                                <Pencil size={16} className="mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-red-600" onClick={() => setConfirmDeleteId(umpire.umpireId)}>
                                <Trash2 size={16} className="mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )
      ) : (
        <div className="text-center text-gray-500 py-12">No umpires found.</div>
      )}
      <EditUmpireModal
        open={editModalOpen}
        umpireId={selectedUmpireId}
        onOpenChange={open => {
          setEditModalOpen(open);
          if (!open) setSelectedUmpireId(null);
        }}
      />
      {confirmDeleteId && (
        <Dialog open={!!confirmDeleteId} onOpenChange={() => setConfirmDeleteId(null)}>
          <DialogContent>
            <DialogTitle>Delete Umpire</DialogTitle>
            <div>Are you sure you want to delete this umpire? This action cannot be undone.</div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => { handleDeleteUmpire(confirmDeleteId!); setConfirmDeleteId(null); }}>Delete</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default UmpiresPage; 